import React, { useState, useEffect, useCallback } from 'react';
import { Button, message, Card, Tag, Typography, Space, Table, DatePicker } from 'antd';
import { ReloadOutlined, ClockCircleOutlined, CheckCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { apiSearchJobTheoNgayVaTeam } from '../services/authService';
import dayjs from 'dayjs';

const { Text } = Typography;

const JobHistoryComponent = () => {
    const [loading, setLoading] = useState(false);
    const [userDepartment, setUserDepartment] = useState('');
    const [jobsData, setJobsData] = useState([]);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    useEffect(() => {
        // Lấy bộ phận từ localStorage
        const job = localStorage.getItem('job');
        if (job) {
            setUserDepartment(job);
        }
    }, []);

    const fetchJobHistory = useCallback(async () => {
        if (!selectedDate) return;

        setLoading(true);
        try {
            const dateStr = selectedDate.format('YYYY-MM-DD');
            const departments = ['Công trình', 'Dự án', 'Máy tính', 'Photocopy'];

            console.log('=== API SEARCH JOB THEO NGAY - ALL DEPARTMENTS ===');
            console.log('Date:', dateStr);
            console.log('Departments:', departments);

            // Gọi API song song cho cả 4 bộ phận
            const promises = departments.map(dept =>
                apiSearchJobTheoNgayVaTeam(dateStr, dept)
                    .then(response => ({ department: dept, data: response, success: true }))
                    .catch(error => ({ department: dept, error, success: false }))
            );

            const results = await Promise.all(promises);

            console.log('All API Results:', results);

            let allJobsData = [];
            let successCount = 0;
            let errorCount = 0;

            // Xử lý kết quả từ từng bộ phận
            results.forEach(result => {
                console.log(`=== ${result.department.toUpperCase()} PROCESSING ===`);
                console.log('Raw result:', result);

                if (result.success) {
                    successCount++;
                    let departmentJobs = [];

                    console.log('Raw data:', result.data);
                    console.log('Data type:', typeof result.data);
                    console.log('Is array:', Array.isArray(result.data));

                    // Xử lý response dựa vào cấu trúc API
                    if (result.data && result.data.data && Array.isArray(result.data.data)) {
                        departmentJobs = result.data.data;
                        console.log('Using result.data.data');
                    } else if (Array.isArray(result.data)) {
                        departmentJobs = result.data;
                        console.log('Using result.data as array');
                    } else if (result.data && typeof result.data === 'object') {
                        // Thử các key khác có thể có
                        departmentJobs = result.data.jobs || result.data.list || result.data.items || [];
                        console.log('Using alternative keys, found:', departmentJobs.length);
                    }

                    console.log('Department jobs before mapping:', departmentJobs);

                    // Thêm thông tin bộ phận vào mỗi job
                    const jobsWithDept = departmentJobs.map(job => ({
                        ...job,
                        department: result.department
                    }));

                    allJobsData = [...allJobsData, ...jobsWithDept];

                    console.log(`=== ${result.department.toUpperCase()} RESULTS ===`);
                    console.log('Jobs found:', departmentJobs.length);
                    console.log('Jobs with department:', jobsWithDept);
                } else {
                    errorCount++;
                    console.log(`=== ${result.department.toUpperCase()} ERROR ===`);
                    console.log('Error:', result.error);
                    if (result.error.response) {
                        console.log('Error response:', result.error.response.data);
                        console.log('Error status:', result.error.response.status);
                    }
                }
            });

            console.log('=== COMBINED RESULTS ===');
            console.log('Total jobs found:', allJobsData.length);
            console.log('Successful departments:', successCount);
            console.log('Failed departments:', errorCount);
            console.log('All jobs data:', allJobsData);

            if (allJobsData.length > 0) {
                console.log('First job:', allJobsData[0]);
                console.log('Job structure keys:', Object.keys(allJobsData[0]));

                // Log chi tiết từng job
                allJobsData.forEach((job, index) => {
                    console.log(`Job ${index + 1}:`, {
                        id: job.id,
                        name: job.name,
                        department: job.department,
                        content: job.noi_dung,
                        status: job.status,
                        start: job.start,
                        end: job.end,
                        note: job.note
                    });
                });
            }

            console.log('===============================');

            // Lưu dữ liệu vào state
            setJobsData(allJobsData);

            if (errorCount === 0) {
                message.success(`Đã tải ${allJobsData.length} công việc từ cả 4 bộ phận ngày ${selectedDate.format('DD/MM/YYYY')}`);
            } else if (successCount > 0) {
                message.warning(`Đã tải ${allJobsData.length} công việc từ ${successCount}/4 bộ phận ngày ${selectedDate.format('DD/MM/YYYY')}`);
            } else {
                message.error('Không thể tải dữ liệu từ bất kỳ bộ phận nào');
            }
        } catch (error) {
            console.log('=== API GET JOB HISTORY ERROR ===');
            console.log('Error:', error);
            console.log('=================================');

            message.error('Đã xảy ra lỗi không mong muốn khi tải dữ liệu');
        } finally {
            setLoading(false);
        }
    }, [selectedDate]);

    // status 0 nhưng chưa có giờ bắt đầu nghĩa là chưa nhận việc, không phải đã hoàn thành
    const getStatusInfo = (record) => {
        if (record.status === 1) {
            return { type: 'in-progress', label: 'Đang thực hiện', color: 'orange', icon: <PlayCircleOutlined /> };
        }
        if (record.status === 0 && !record.start) {
            return { type: 'not-received', label: 'Chưa nhận việc', color: 'red', icon: null };
        }
        if (record.status === 0) {
            return { type: 'completed', label: 'Đã hoàn thành', color: 'green', icon: <CheckCircleOutlined /> };
        }
        if (record.status === -1) {
            return { type: 'not-received', label: 'Chưa nhận việc', color: 'red', icon: null };
        }
        return { type: 'unknown', label: 'Không xác định', color: 'default', icon: null };
    };

    const formatTime = (timeStr) => {
        if (!timeStr) return '-';
        return dayjs(`2024-01-01 ${timeStr}`).format('HH:mm');
    };

    const formatDateTime = (dateTimeStr) => {
        if (!dateTimeStr) return '-';
        return dayjs(dateTimeStr).format('HH:mm');
    };

    const getWorkDuration = (start, end) => {
        if (!start) return '-';
        if (!end) return 'Đang làm';

        const startTime = dayjs(`2024-01-01 ${start}`);
        const endTime = dayjs(`2024-01-01 ${end}`);
        const duration = endTime.diff(startTime, 'minute');

        if (duration < 60) {
            return `${duration} phút`;
        } else {
            const hours = Math.floor(duration / 60);
            const minutes = duration % 60;
            return `${hours}h ${minutes}p`;
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            align: 'center',
            render: (id) => <Text strong style={{ fontSize: '14px' }}>{id}</Text>
        },
        {
            title: 'Nhân viên',
            dataIndex: 'name',
            key: 'name',
            width: 150,
            render: (name) => (
                <Text style={{ fontSize: '14px' }}>{name}</Text>
            )
        },
        {
            title: 'Bộ phận',
            dataIndex: 'department',
            key: 'department',
            width: 110,
            align: 'center',
            render: (department) => {
                let color = 'blue';
                if (department === 'Máy tính') color = 'orange';
                else if (department === 'Công trình') color = 'green';
                else if (department === 'Dự án') color = 'purple';
                else if (department === 'Photocopy') color = 'cyan';

                return (
                    <Tag color={color} style={{ fontSize: '12px' }}>
                        {department}
                    </Tag>
                );
            }
        },
        {
            title: 'Trạng thái',
            dataIndex: 'status',
            key: 'status',
            width: 150,
            align: 'center',
            render: (_, record) => {
                const info = getStatusInfo(record);
                return <Tag color={info.color} icon={info.icon}>{info.label}</Tag>;
            }
        },
        {
            title: 'Nội dung công việc',
            dataIndex: 'noi_dung',
            key: 'noi_dung',
            render: (content) => (
                <Text style={{ fontSize: '14px' }}>{content}</Text>
            )
        },
        {
            title: 'Thời gian tạo',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 140,
            align: 'center',
            render: (created_at) => (
                <Text style={{ fontSize: '14px' }}>
                    <ClockCircleOutlined /> {formatDateTime(created_at)}
                </Text>
            )
        },
        {
            title: 'Thời gian bắt đầu',
            dataIndex: 'start',
            key: 'start',
            width: 110,
            align: 'center',
            render: (start) => (
                <Text style={{ fontSize: '14px', color: '#52c41a' }}>
                    <ClockCircleOutlined /> {formatTime(start)}
                </Text>
            )
        },
        {
            title: 'Thời gian kết thúc',
            dataIndex: 'end',
            key: 'end',
            width: 110,
            align: 'center',
            render: (end) => (
                <Text style={{ fontSize: '14px', color: '#ff4d4f' }}>
                    <ClockCircleOutlined /> {formatTime(end)}
                </Text>
            )
        },
        {
            title: 'Tổng thời gian',
            key: 'duration',
            width: 110,
            align: 'center',
            render: (_, record) => {
                const info = getStatusInfo(record);
                let color = '#faad14'; // Mặc định vàng cho đang thực hiện
                if (info.type === 'completed') {
                    color = '#52c41a'; // Xanh cho hoàn thành
                } else if (info.type === 'not-received') {
                    color = '#ff4d4f'; // Đỏ cho chưa nhận việc
                }
                return (
                    <Text
                        strong
                        style={{
                            fontSize: '14px',
                            color: color
                        }}
                    >
                        {getWorkDuration(record.start, record.end)}
                    </Text>
                );
            }
        },
        {
            title: 'Ghi chú',
            dataIndex: 'note',
            key: 'note',
            width: 180,
            ellipsis: true,
            render: (note) => (
                <Text
                    style={{ fontSize: '14px', color: '#000' }}
                    title={note}
                >
                    {note || '-'}
                </Text>
            )
        }
    ];

    // Auto load khi có selectedDate
    useEffect(() => {
        if (selectedDate) {
            fetchJobHistory();
        }
    }, [selectedDate, fetchJobHistory]);

    return (
        <div>
            <style>
                {`
                    .completed-row {
                        background-color: #f6ffed !important;
                    }
                    .completed-row:hover {
                        background-color: #d9f7be !important;
                    }
                    .in-progress-row {
                        background-color: #fff7e6 !important;
                    }
                    .in-progress-row:hover {
                        background-color: #ffe7ba !important;
                    }
                    .not-received-row {
                        background-color: #fff2f0 !important;
                    }
                    .not-received-row:hover {
                        background-color: #ffccc7 !important;
                    }
                `}
            </style>
            <Card title="Lịch sử công việc tất cả bộ phận theo ngày">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
                    <Space size="large" wrap>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Bộ phận:</span>
                            <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                Tất cả (Máy tính, Công trình, Dự án, Photocopy)
                            </Tag>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ fontWeight: 'bold', marginRight: '8px' }}>Ngày:</span>
                            <DatePicker
                                value={selectedDate}
                                onChange={(date) => setSelectedDate(date)}
                                format="DD/MM/YYYY"
                                placeholder="Chọn ngày"
                                style={{ width: '140px' }}
                            />
                        </div>
                    </Space>

                    <Button
                        type="primary"
                        icon={<ReloadOutlined />}
                        onClick={fetchJobHistory}
                        loading={loading}
                    >
                        Tải lại
                    </Button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Text style={{ fontSize: '16px' }}>Đang tải dữ liệu...</Text>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: '12px', textAlign: 'right' }}>
                            <Text type="secondary" style={{ fontSize: '14px' }}>
                                Tổng {jobsData.length} công việc
                            </Text>
                        </div>

                        <Table
                            columns={columns}
                            dataSource={jobsData}
                            rowKey="id"
                            pagination={false}
                            scroll={{ x: 1230, y: 600 }}
                            tableLayout="fixed"
                            locale={{
                                emptyText: 'Không có công việc nào từ cả 4 bộ phận trong ngày này'
                            }}
                            size="middle"
                            bordered
                            rowClassName={(record) => {
                                const info = getStatusInfo(record);
                                if (info.type === 'completed') {
                                    return 'completed-row';
                                } else if (info.type === 'in-progress') {
                                    return 'in-progress-row';
                                } else if (info.type === 'not-received') {
                                    return 'not-received-row';
                                }
                                return '';
                            }}
                        />
                    </>
                )}
            </Card>
        </div>
    );
};

export default JobHistoryComponent;
